import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { decamelize } from 'humps';
import { Modal, Icon, NewButton } from '../../../../../components';
import { DataDictionaryTabs } from '../DataDictionaryTabs';
import { DataDictionarySourceView } from '../DataDictionarySourceView';
import { DataDictionaryTermTranslationsView } from '../DataDictionaryTermTranslationsView';
import { DataDictionaryMetricsView } from '../DataDictionaryMetricsView';
import { DataDictionaryModalFooter } from '../../molecules';
import {
  databaseDescriptionShape,
  sourceShape,
  termTranslationShape
} from '../../../../../propsShape';

const DataDictionaryModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
`;

const DataDictionaryModalBody = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px - 72px - 95px);
`;

const DataDictionaryModal = ({
  databaseDescription,
  insertMention,
  sources,
  termTranslations,
  isReadOnly = false
}) => {
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeView, setActiveView] = useState('sources');

  const handleViewChange = useCallback((view) => setActiveView(view), [setActiveView]);

  const onToggleColumnCheckbox = useCallback((newItem) => {
    setSelectedItems((prevSelectedItems) => {
      const exists = prevSelectedItems.some(
        (item) =>
          item.value === newItem.value && item.table === newItem.table && item.tag === newItem.tag
      );

      return exists
        ? prevSelectedItems.filter(
            (item) =>
              !(
                item.value === newItem.value &&
                item.table === newItem.table &&
                item.tag === newItem.tag
              )
          )
        : [...prevSelectedItems, newItem];
    });
  }, []);

  const onAddMentions = useCallback(() => {
    selectedItems.forEach((column) =>
      insertMention({
        trigger: '@',
        value: decamelize(`${column.table}.${column.value}`),
        data: {
          view: decamelize(column.table.split('.')[1]),
          schema: decamelize(column.table.split('.')[0]),
          column: decamelize(column.value),
          columnDescription: column.columnDescription,
          enumeratedValues: column.enumeratedValues,
          source: sources?.find(
            (source) => source.integration.schema === column.table.split('.')[0]
          )
        }
      })
    );
    setSelectedItems([]);
  }, [selectedItems, insertMention, selectedTable]);

  const handleAddTableMentions = useCallback(
    (close) => {
      close();
      onAddMentions();
    },
    [onAddMentions]
  );

  const [businessLogicTermTranslations, metricsTermTranslations] = useMemo(() => {
    const business = termTranslations?.filter((tt) => tt?.type === null) || [];
    const metrics = termTranslations?.filter((tt) => tt?.type === 'metric') || [];
    return [business, metrics];
  }, [termTranslations]);

  return (
    <Modal size='lg' closeOnBackdropClick>
      {(close) => (
        <>
          <DataDictionaryModalHeader>
            <DataDictionaryTabs
              activeView={activeView}
              onViewChange={handleViewChange}
              isTermTranslationsTabAvailable={businessLogicTermTranslations.length > 0}
              isMetricsTabAvailable={metricsTermTranslations.length > 0}
            />
            <NewButton tertiary onClick={close}>
              <Icon name='close' />
            </NewButton>
          </DataDictionaryModalHeader>
          <DataDictionaryModalBody>
            {activeView === 'sources' && (
              <DataDictionarySourceView
                onToggleColumnCheckbox={onToggleColumnCheckbox}
                databaseDescription={databaseDescription}
                selectedTable={selectedTable}
                selectedItems={selectedItems}
                setSelectedTable={setSelectedTable}
                sources={sources}
                isReadOnly={isReadOnly}
              />
            )}
            {activeView === 'termTranslations' && (
              <DataDictionaryTermTranslationsView
                termTranslations={termTranslations.filter((tt) => tt.type === null)}
              />
            )}
            {activeView === 'metrics' && (
              <DataDictionaryMetricsView
                termTranslations={termTranslations.filter((tt) => tt.type === 'metric')}
                selectedItems={selectedItems}
                onToggleColumnCheckbox={onToggleColumnCheckbox}
              />
            )}
          </DataDictionaryModalBody>
          {!isReadOnly && (
            <DataDictionaryModalFooter
              close={close}
              handleAddTableMentions={handleAddTableMentions}
              selectedItems={selectedItems}
              onDelete={onToggleColumnCheckbox}
            />
          )}
        </>
      )}
    </Modal>
  );
};

DataDictionaryModal.propTypes = {
  databaseDescription: databaseDescriptionShape.isRequired,
  insertMention: PropTypes.func.isRequired,
  sources: PropTypes.arrayOf(sourceShape).isRequired,
  termTranslations: PropTypes.arrayOf(termTranslationShape).isRequired,
  isReadOnly: PropTypes.bool
};

export { DataDictionaryModal };
